import {
  PathRef,
  ReactEditor,
  useSlateStatic,
  useSlateSelection,
} from 'domains/reporter/RichTextEditor/core';
import WarningIcon from '@mui/icons-material/Warning';
import omit from 'ramda/es/omit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { css } from 'styled-components';
import { Editor } from 'slate';
import { Colors } from 'styles';
import { useRecoilValue } from 'recoil';
import { touchedRequiredFieldsState } from '../../Reporter/state';
import { logger } from 'modules/logger';
import { getPathRefSafe } from '../utils/refHelpers';
import { env } from 'config/env';
import Tooltip from 'common/ui/Tooltip';
import { useUnifiedEditorElement } from './UnifiedEditorElementContext';
import { useCoalescedAnimationFrame } from '../hooks/useCoalescedAnimationFrame';
import { type RangeSnapshot, setReferenceElementIfRangeChanged } from './utils/rangeSnapshot';

const Indicator = ({
  pathRef,
  updateToken,
}: {
  pathRef: PathRef;
  updateToken: number;
}): React.ReactElement | null => {
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [referenceElement, setReferenceElement] = useState<globalThis.Range | null>(null);
  const previousRangeSnapshot = useRef<Nullable<RangeSnapshot>>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);
  const unifiedEditorElement = useUnifiedEditorElement();
  const popperReference = useMemo(() => {
    if (referenceElement == null) {
      return null;
    }

    const contextElement =
      referenceElement.commonAncestorContainer instanceof Element
        ? referenceElement.commonAncestorContainer
        : referenceElement.commonAncestorContainer.parentElement;

    return {
      getBoundingClientRect: () => referenceElement.getBoundingClientRect(),
      contextElement: contextElement ?? undefined,
    };
  }, [referenceElement]);
  const { styles, state } = usePopper(popperReference, popperElement, {
    placement: 'right',
    modifiers: [
      { name: 'offset', options: { offset: [0, 10] } },
      { name: 'arrow', options: { element: arrowElement } },
    ],
  });
  const editor = useSlateStatic();

  const updateReferenceElement = useCallback(() => {
    const setReferenceElementIfChanged = (nextRange: globalThis.Range | null) =>
      setReferenceElementIfRangeChanged({
        nextRange,
        previousRangeSnapshotRef: previousRangeSnapshot,
        setReferenceElement,
      });
    if (editor == null || pathRef.current == null) {
      setReferenceElementIfChanged(null);
      return;
    }
    try {
      const ref = getPathRefSafe(pathRef);
      const domRange = ReactEditor.toDOMRange(editor, Editor.range(editor, ref));
      setReferenceElementIfChanged(domRange);
    } catch (e: any) {
      logger.error('Failed to get dom range for required field indicator', e);
      setReferenceElementIfChanged(null);
    }
  }, [editor, pathRef]);

  useEffect(() => {
    updateReferenceElement();
  }, [updateReferenceElement, updateToken]);

  if (referenceElement == null) {
    return null;
  }

  const popperStyles = omit(['transform'], styles.popper || {});
  const arrowStyles = omit(['transform'], styles.arrowStyles || {});
  const popperOffsetY = state?.modifiersData?.popperOffsets?.y
    ? Math.round(state?.modifiersData?.popperOffsets?.y + 2)
    : null;
  const outOfView =
    referenceElement?.getBoundingClientRect().bottom >
      (unifiedEditorElement?.getBoundingClientRect()?.bottom ?? 0) - 32 ||
    referenceElement?.getBoundingClientRect().top <
      (unifiedEditorElement?.getBoundingClientRect()?.top ?? 0);

  return (
    <Tooltip content="Required field">
      <div
        ref={setPopperElement}
        {...(state?.attributes?.popper != null ? state.attributes.popper : {})}
        style={{
          margin: 0,
          ...(popperOffsetY != null ? { transform: `translate(0px, ${popperOffsetY}px)` } : {}),
        }}
        css={css`
          ${css(popperStyles)};
          background-color: transparent;
          display: flex;
          align-content: center;
          z-index: 0;
          height: 35px;
        `}
      >
        <div
          css={css`
            ${css(arrowStyles)};
            display: flex;
            align-content: center;
          `}
          ref={setArrowElement}
        >
          <WarningIcon
            data-testid="required-field-indicator"
            css={css`
              color: ${Colors.red5};
              font-size: 1.6rem;
              position: relative;
              left: 10px;
              top: 7px;
              display: ${(popperOffsetY != null && !outOfView) ||
              env.STORYBOOK_STORYSHOTS === 'true'
                ? 'block'
                : 'none'};
            `}
          />
        </div>
      </div>
    </Tooltip>
  );
};

export const RequiredFieldIndicators = (): React.ReactElement | null => {
  const touchedRequiredFields = useRecoilValue(touchedRequiredFieldsState);
  const selection = useSlateSelection();
  const [updateToken, setUpdateToken] = useState(0);
  const schedulePositionUpdateNow = useCallback(() => {
    setUpdateToken((token) => token + 1);
  }, []);
  const { schedule: schedulePositionUpdate } =
    useCoalescedAnimationFrame(schedulePositionUpdateNow);
  const hasTouchedRequiredFields = touchedRequiredFields.size > 0;

  useEffect(() => {
    if (!hasTouchedRequiredFields) {
      return;
    }

    document.addEventListener('selectionchange', schedulePositionUpdate);
    window.addEventListener('scroll', schedulePositionUpdate, true);
    window.addEventListener('resize', schedulePositionUpdate);

    return () => {
      document.removeEventListener('selectionchange', schedulePositionUpdate);
      window.removeEventListener('scroll', schedulePositionUpdate, true);
      window.removeEventListener('resize', schedulePositionUpdate);
    };
  }, [hasTouchedRequiredFields, schedulePositionUpdate]);

  useEffect(() => {
    if (!hasTouchedRequiredFields) {
      return;
    }

    schedulePositionUpdate();
  }, [hasTouchedRequiredFields, schedulePositionUpdate, selection, touchedRequiredFields]);

  if (touchedRequiredFields.size === 0) {
    return null;
  }

  const indicators = Array.from(touchedRequiredFields).map(
    ([reactKey, pathRef]: [any, PathRef]) => (
      <Indicator key={reactKey.id} pathRef={pathRef} updateToken={updateToken} />
    )
  );

  return indicators.length > 0 ? <>{indicators}</> : null;
};
